/**
 * components/hazard/PhotoSection.js — Зураг оруулах + харуулах хэсэг.
 *
 * Оруулсан зургууд thumbnail (92×92) хэлбэрээр эгнэнэ. Тус бүр дээр X товч
 * байх ба дарахад тухайн зургийг устгана. Зураг дээр дархад БҮТЭН хэмжээгээр
 * харуулна (lightbox). Төгсгөлд "Нэмэх" хайрцаг. (Мобайл PhotoStrip-тэй ижил UX.)
 *
 * Props:
 *   photos          : array of { file_url, file_name }
 *   onPhotosChange  : (next: array) => void
 *   onPickFile      : (file: File) => Promise<{file_url, file_name}>  — upload
 *   uploading       : boolean
 */
import ThumbImg from "src/utils/mediaThumb";
import React, { useRef, useState } from "react";
import Swal from "sweetalert2";
import { Card, SectionTitle, RED } from "./common";
import { guardFile } from "src/components/MediaUpload";

const THUMB = 76;

export default function PhotoSection({
  photos = [],
  onPhotosChange,
  onPickFile,
  uploading = false,
}) {
  /* ⚠ ХОЁР ТУСДАА input. Нэг input дээр `capture` тавибал мобайл хөтөч
     ГАЛЕРЕЙГ ОГТ САНАЛ БОЛГОХГҮЙ, шууд камер нээнэ. Түүнчлэн `capture` ба
     `multiple` хоёр нь зөрчилддөг (нэг камерын агшин = нэг файл) тул зарим
     Android/iOS хөтөч сонгогчийг огт нээхгүй байв — «зураг авах ажиллахгүй»
     гэдгийн шалтгаан. Иймд:
        cameraRef  — камер, нэг зураг (capture)
        galleryRef — зургийн сан, олон зураг (capture БАЙХГҮЙ) */
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  // Томруулж харах зургийн URL (null бол хаалттай)
  const [preview, setPreview] = useState(null);

  const handlePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const uploaded = [];
      for (const f of files) {
        // Хэт том зурагт анхааруулна (components/MediaUpload.js).
        // Ажилтан талбар дээр сул сүлжээтэй байдаг тул 10 MB зураг
        // байршуулах гэж удаан хүлээхээс өмнө мэдэгдэх нь чухал.
        // eslint-disable-next-line no-await-in-loop
        if (!(await guardFile(f))) continue;
        // eslint-disable-next-line no-await-in-loop
        const r = await onPickFile?.(f);
        if (r?.file_url) uploaded.push(r);
      }
      onPhotosChange?.([...photos, ...uploaded]);
    } catch (err) {
      Swal.fire({ title: "Зураг хадгалж чадсангүй", icon: "error" });
    } finally {
      e.target.value = "";
    }
  };

  // Тодорхой индексийн зургийг устгах
  const removePhoto = (index) => {
    onPhotosChange?.(photos.filter((_, i) => i !== index));
  };

  return (
    <Card>
      <SectionTitle right={uploading ? "Илгээгдэж байна..." : null}>
        Зураг хавсаргах
      </SectionTitle>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {photos.map((p, i) => (
          <div
            key={i}
            style={{
              position: "relative",
              width: THUMB,
              height: THUMB,
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid #E6E9EF",
              background: "#000",
            }}
          >
            {/* ⚠ Нүд нь жижиг боловч ЭХ ХУВЬ татаж байв (хэмжсэн: 3.93 MB
                vs 12 KB). Медиа нь Flask-аар дамждаг тул зураг бүр
                worker эзэлж, олон зурагтай хуудас API-г бүхэлд нь
                гацаадаг. Дарахад эх хувь нээгдсэн хэвээр. */}
            <ThumbImg
              full={p.file_url}
              alt={p.file_name || `photo-${i}`}
              onClick={() => setPreview(p.file_url)}
              title="Томруулж харах"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: "zoom-in",
                display: "block",
              }}
            />
            <button
              type="button"
              onClick={() => removePhoto(i)}
              title="Устгах"
              aria-label="Зураг устгах"
              style={{
                position: "absolute",
                top: 3,
                right: 3,
                zIndex: 2,
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: "1.5px solid #fff",
                background: RED,
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                fontSize: 10,
                lineHeight: 1,
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        ))}

        {/* Нэмэх — КАМЕР ба ЗУРГИЙН САН тусдаа. Гар утсан дээр аль
            алиныг нь ашиглах боломжтой байх ёстой (талбар дээр шууд авах,
            эсвэл өмнө авсан зургаа хавсаргах). */}
        {[
          {
            key: "camera",
            icon: "fa-camera",
            label: "Камер",
            onClick: () => cameraRef.current?.click(),
          },
          {
            key: "gallery",
            icon: "fa-images",
            label: "Сангаас",
            onClick: () => galleryRef.current?.click(),
          },
        ].map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={b.onClick}
            disabled={uploading}
            style={{
              width: THUMB,
              height: THUMB,
              borderRadius: 10,
              border: "2px dashed #DDE1E8",
              background: "#FAFBFC",
              color: "#8A94A6",
              cursor: uploading ? "default" : "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <i className={"fas " + b.icon} style={{ fontSize: 18 }} />
            {uploading ? "..." : b.label}
          </button>
        ))}
      </div>

      {photos.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#8A94A6" }}>
          {photos.length} зураг хавсаргасан
        </div>
      )}

      {/* КАМЕР — `capture` нь ард талын камерыг шууд нээнэ. `multiple`
          ТАВИХГҮЙ: нэг агшин = нэг зураг, хоёуланг нь зэрэг өгвөл зарим
          хөтөч сонгогчийг огт нээхгүй. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handlePick}
      />
      {/* ЗУРГИЙН САН — `capture` БАЙХГҮЙ тул галерей нээгдэнэ, олон зураг
          сонгож болно. Компьютер дээр ч энэ нь ердийн файл сонгогч. */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handlePick}
      />

      {/* Томруулж харах — бүтэн дэлгэцийн overlay (дарж хаана) */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            cursor: "zoom-out",
          }}
        >
          <img
            src={preview}
            alt="preview"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: 6,
              boxShadow: "0 6px 30px rgba(0,0,0,0.5)",
            }}
          />
          <button
            type="button"
            onClick={() => setPreview(null)}
            aria-label="Хаах"
            style={{
              position: "fixed",
              top: 16,
              right: 20,
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "none",
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}
    </Card>
  );
}
